/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-shadow */
import './styles.css';

import React, { useEffect, useMemo } from 'react';
import { errorCatcherWrap } from '@search/error/src/catcher';
import { ApplicationIdEnum } from '@search/graphql-typings';
import {
    useOfferFiltersDesktop
} from '@search/vtbeco-frontend-core/domain/offer/filters/useOfferFiltersDesktop';
import { OfferYMapDesktop } from '@search/vtbeco-frontend-core/domain/offer/ymap/Desktop/Desktop';
import { OfferYMapUnionPlacemark } from '@search/vtbeco-frontend-core/domain/offer/ymap/union/UnionPlacemark';
import { YMapClusterer } from '@search/ymap/src/clusterer/YMapClusterer';
import {
    useOfferYMapPageState2
} from '@search/vtbeco-frontend-core/domain/offer/ymap/page/useOfferYMapPageState2';
import { SearchMapRoute, SearchRoute, useRouter } from '@search/offer-search-routes/src';
import classname from '@search/classname/src';

import { RegionIdEnum } from '@search/filter-enums/enums/Region';
import { RegionFilter, MapBoundsFilter } from '@search/vtbeco-frontend-core/view/filters/models/Filter';
import { ClassifiedFilterCollection } from '@search/vtbeco-frontend-core/domain/offer/filters/ClassifiedFilterCollection';
import { useFilterCollapser } from '@search/vtbeco-frontend-core/domain/newbuilding/components/desktop/FilterCollapser';
import { SaveSearch } from '@search/classified-common/src/SaveSearch';
import { useSaveSearchState } from '@search/classified-common/src/SaveSearch/useSaveSearchState';
import { useClassifiedOfferDataLayerPush } from '@search/vtbeco-frontend-core/domain/offer/analytics/ClassifiedOfferGtmContext';
import { offerFilterGqlFromRoute } from '@search/vtbeco-frontend-core/domain/offer/search/page/offerFilterGqlFromRoute';
import { CustomerFeedback } from '@search/vtbeco-frontend-core/view/common/components/CustomerFeedback';
// import { MortgageButton } from '@search/vtbeco-frontend-core/view/common/components/MortgageButton';

import { FilterContainer } from './FilterContainer/FilterContainer';
import { OfferSearchMapList } from './OfferSearchMapList/OfferSearchMapList';

function isWindowWidthLesserMBreakpoint() {
    return typeof document !== 'undefined' ?
        // eslint-disable-next-line max-len
        document.body.offsetWidth <= Number(getComputedStyle(document.documentElement).getPropertyValue('--screen-m-end').slice(0, -2)) :
        1199;
}

const cnOffersSearch = classname.bind(null, 'OfferSearchMap');

export const OfferSearchMap = errorCatcherWrap(function OfferSearchMap() {
    const router = useRouter();
    const searchMapRoute = router.route(SearchMapRoute);
    const searchMapParams = searchMapRoute.params();
    const searchRoute = router.route(SearchRoute);
    const gqlFilters = offerFilterGqlFromRoute(searchMapParams);

    const {
        geoStore,
        count,
        filters,
        collection,
        clusterPoints,
        tileUrl,
        bounds,
        setBounds,
        polygon,
        setPolygon,
        updateFilters,
        addQueryDisplayValue,
        queryDisplayValues,
        tips,
        selected,
        clearSelected,
        handleSubmit,
        handleResetInSegment,
        loading,
        isDrawAreaVisible, toggleDrawArea, closeDrawArea,
        headerTitle,
        showPromoRieltors
    } = useOfferYMapPageState2(searchMapRoute);

    const {
        removeGeo,
        closeDrawAreaRef
    } = useOfferFiltersDesktop({
        updateFilters,
        filters,
        geoStore,
        queryDisplayValues
    });

    const {
        isOpenSaveSearch,
        openSaveSearch,
        closeSaveSearch
    } = useSaveSearchState();

    const dataLayerPush = useClassifiedOfferDataLayerPush();

    closeDrawAreaRef.current = closeDrawArea;

    const regionUpdated = React.useCallback((region: RegionIdEnum) => {
        const regionFilter = new RegionFilter(region);

        updateFilters(filters.update(regionFilter).update(new MapBoundsFilter()) as ClassifiedFilterCollection);
        closeDrawArea();
    }, [ closeDrawArea, filters, updateFilters ]);

    const handleClickList = React.useCallback(() => {
        searchRoute.push({ ...searchMapParams, selectedOffers: undefined });
    }, [ searchRoute, searchMapParams ]);

    const filterPanelState = useFilterCollapser({
        reset: handleResetInSegment,
        clearSelected,
        initCollapsed: Boolean(
            searchMapParams.mapBounds?.geohash ||
            searchMapParams.selectedOffers?.length ||
            isWindowWidthLesserMBreakpoint()
        )
    });

    const rollUpFilter = React.useCallback(() => {
        if (! filterPanelState.collapsed && isWindowWidthLesserMBreakpoint()) {
            filterPanelState.toggleCollapsed();
        }
    }, [ filterPanelState ]);

    useEffect(() => {
        window.addEventListener('resize', rollUpFilter);

        return () => window.removeEventListener('resize', rollUpFilter);
    }, [ rollUpFilter ]);

    const items = useMemo(() => {
        return collection?.map(item => ({
            ...item,
            onClick: (e: MouseEvent, el?: HTMLElement) => {
                filterPanelState.close();
                item?.onClick(e, el);
            }
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ collection, filterPanelState.close ]);

    const hasSidebar = ! filterPanelState.collapsed ||
        Boolean(selected && (selected.geoHash || selected.selectedOffers));

    return (
        <div className={cnOffersSearch()}>
            <OfferYMapDesktop
                altLayout
                saveSearch={() => {
                    openSaveSearch();
                    dataLayerPush({ event: 'classified_map_click_save_search' });
                }}
                bounds={bounds}
                toggleDrawArea={toggleDrawArea}
                isDrawAreaVisible={isDrawAreaVisible}
                selectedArea={polygon}
                setBounds={setBounds}
                setPolygon={setPolygon}
                handleClickList={handleClickList}
                mapUrl={searchMapRoute.url(searchMapParams)}
                listUrl={searchRoute.url(searchMapParams)}
                tileUrl={tileUrl}
                hasSidebar={hasSidebar}
                renderCustomerFeedback={<CustomerFeedback applicationId={ApplicationIdEnum.ClassifiedDesktop} />}
                // eslint-disable-next-line max-len
                // renderMortgageButton={(typeof window !== 'undefined' && sessionStorage.getItem('mortgagePin')) || ! filters.isFlatFilterActive() ?
                //     null :
                //     <MortgageButton handleClick={handleMortgageButtonClick} />
                // }
            >
                <FilterContainer
                    handleReset={filterPanelState.reset}
                    collapsed={filterPanelState.collapsed}
                    toggleCollapsed={filterPanelState.toggleCollapsed}
                    count={count ?? 0}
                    geoStore={geoStore}
                    filters={filters}
                    tips={tips}
                    loading={loading}
                    handleSubmit={handleSubmit}
                    removeTips={removeGeo}
                    regionUpdated={regionUpdated}
                    addQueryDisplayValue={addQueryDisplayValue}
                    updateFilters={updateFilters}
                    showPromoRieltors={showPromoRieltors}
                />
                {selected && <OfferSearchMapList
                    selected={selected}
                    handleClose={clearSelected}
                />}
                {items?.map(item => <OfferYMapUnionPlacemark
                    item={item}
                    key={item?.id ?? `${item.coordinates.latitude}${item.coordinates.longitude}`}
                />)}
                <YMapClusterer points={clusterPoints} />
                {isOpenSaveSearch ? (
                    <SaveSearch
                        handleClose={closeSaveSearch}
                        searchTitle={headerTitle}
                        openSaveSearch={openSaveSearch}
                        gqlFilters={gqlFilters}
                    />
                ) : null}
            </OfferYMapDesktop>
        </div>
    );
});
